import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;

function OnboardingWorkflowPage({ user, onLogout }) {
  const [checklists, setChecklists] = useState([]);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  useEffect(() => {
    fetchChecklists();
    fetchEmployees();
  }, []);

  const fetchChecklists = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/onboarding/checklists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChecklists(response.data.checklists || []);
    } catch (error) {
      console.error('Error fetching checklists:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const createChecklist = async () => {
    if (!selectedEmployeeId) {
      alert('Please select an employee');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/onboarding/checklists`,
        { employee_id: selectedEmployeeId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert('Onboarding checklist created successfully!');
      setShowCreateForm(false);
      setSelectedEmployeeId('');
      fetchChecklists();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const completeTask = async (checklistId, taskId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/onboarding/checklists/${checklistId}/tasks/${taskId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      fetchChecklists();
      if (selectedChecklist?.id === checklistId) {
        const response = await axios.get(
          `${API}/api/onboarding/checklists/${checklistId}`,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        setSelectedChecklist(response.data.checklist);
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const viewChecklist = async (checklistId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/onboarding/checklists/${checklistId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setSelectedChecklist(response.data.checklist);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'documentation': return '📄';
      case 'it_setup': return '💻';
      case 'training': return '🎯';
      case 'compliance': return '✅';
      default: return '📋';
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Onboarding Workflow</h1>
            <p className="mt-2 text-gray-600">Track employee onboarding progress and tasks</p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition"
          >
            + Create Checklist
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="text-sm text-blue-600 font-medium">In Progress</p>
            <p className="text-2xl font-bold text-blue-900">
              {checklists.filter(c => c.status === 'in_progress').length}
            </p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-sm text-green-600 font-medium">Completed</p>
            <p className="text-2xl font-bold text-green-900">
              {checklists.filter(c => c.status === 'completed').length}
            </p>
          </div>
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <p className="text-sm text-red-600 font-medium">Overdue</p>
            <p className="text-2xl font-bold text-red-900">
              {checklists.filter(c => c.status === 'overdue').length}
            </p>
          </div>
        </div>

        {/* Checklists Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Checklists List */}
          <div>
            <h2 className="text-xl font-bold mb-4">Onboarding Checklists</h2>
            {checklists.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
                <p className="mb-4">No onboarding checklists yet</p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="px-6 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition"
                >
                  Create First Checklist
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {checklists.map((checklist) => (
                  <div
                    key={checklist.id}
                    className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition cursor-pointer"
                    onClick={() => viewChecklist(checklist.id)}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{checklist.employee_name}</h3>
                        <p className="text-sm text-gray-500">Joining: {new Date(checklist.joining_date).toLocaleDateString()}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(checklist.status)}`}>
                        {checklist.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <div className="mb-2">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Progress</span>
                        <span className="font-semibold">{checklist.progress_percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-[#2b3380] h-2 rounded-full transition-all duration-300"
                          style={{ width: `${checklist.progress_percentage}%` }}
                        />
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      {checklist.completed_tasks || 0} of {checklist.total_tasks || 0} tasks completed
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Checklist Details */}
          <div>
            <h2 className="text-xl font-bold mb-4">Checklist Details</h2>
            {selectedChecklist ? (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedChecklist.employee_name}</h3>
                  <div className="flex gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedChecklist.status)}`}>
                      {selectedChecklist.status.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {selectedChecklist.progress_percentage}% Complete
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  {selectedChecklist.tasks?.map((task) => (
                    <div
                      key={task.id}
                      className={`border-2 rounded-lg p-4 ${
                        task.is_completed ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          checked={task.is_completed}
                          onChange={() => !task.is_completed && completeTask(selectedChecklist.id, task.id)}
                          disabled={task.is_completed || loading}
                          className="mt-1 mr-3 h-5 w-5"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xl">{getCategoryIcon(task.category)}</span>
                            <h4 className={`font-semibold ${
                              task.is_completed ? 'text-green-900 line-through' : 'text-gray-900'
                            }`}>
                              {task.title}
                            </h4>
                            {task.is_mandatory && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Required</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                          <div className="flex gap-3 text-xs text-gray-500">
                            <span>👤 {task.assigned_to.toUpperCase()}</span>
                            <span>📅 {task.deadline_days} days</span>
                            {task.is_completed && task.completed_at && (
                              <span className="text-green-600">
                                ✅ {new Date(task.completed_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
                <div className="text-6xl mb-4">📋</div>
                <p>Select a checklist to view details</p>
              </div>
            )}
          </div>
        </div>

        {/* Create Checklist Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Create Onboarding Checklist</h2>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Employee *</label>
                    <select
                      value={selectedEmployeeId}
                      onChange={(e) => setSelectedEmployeeId(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                    >
                      <option value="">Choose an employee...</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_id})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setShowCreateForm(false)}
                      className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={createChecklist}
                      disabled={loading || !selectedEmployeeId}
                      className="flex-1 px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition disabled:bg-gray-400"
                    >
                      {loading ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    💡 A default checklist with common onboarding tasks will be created automatically
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default OnboardingWorkflowPage;